import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { find, getOr } from 'lodash/fp';
import { createSelector } from 'reselect';

import { getOrEmptyArray, getOrEmptyObject, getStringProp, values } from '@mews/fp';
import { isDefined } from '@mews/std';

import { getEntities } from '../getEntities';
import { ExchangeRate } from './types';

const getOrOne = getOr<number>(1);

const getExchangeRates = createSelector(getEntities, getOrEmptyObject('exchangeRates'));

const getExchangeRatesData = createSelector(getExchangeRates, getOrEmptyObject('data'));

const getExchangeRatesMeta = createSelector(getExchangeRates, getOrEmptyObject('meta'));

const getExchangeRatesMetaFlatValues = createSelector(getExchangeRatesMeta, flow(values, array.flatMap(values)));

export const areAnyExchangeRatesFetching = createSelector(
    getExchangeRatesMetaFlatValues,
    array.some(({ isFetching }) => isFetching)
);

export const getFailedExchangeRates = createSelector(
    getExchangeRatesMetaFlatValues,
    array.filter(({ hasFailed }) => hasFailed)
);

export const hasAnyExchangeRatesFailed = createSelector(
    getFailedExchangeRates,
    (failedExchangeRates) => !array.isEmpty(failedExchangeRates)
);

const getExchangeRatesItem = createSelector(
    getStringProp('enterpriseId'),
    getExchangeRatesData,
    (enterpriseId: string, exchangeRates) => exchangeRates?.[enterpriseId]
);

const getEnterpriseExchangeRates = createSelector(getExchangeRatesItem, (exchangeRates) =>
    getOrEmptyArray('enterpriseExchangeRates', exchangeRates)
);

const getPlatformExchangeRates = createSelector(getExchangeRatesItem, (exchangeRates) =>
    getOrEmptyArray('platformExchangeRates', exchangeRates)
);

export const hasExchangeRates = createSelector(
    getExchangeRatesItem,
    getStringProp('currencyCode'),
    (exchangeRates, currencyCode) =>
        pipe(
            exchangeRates,
            option.fromNullable,
            option.map((rates) => rates.currencyCodes.includes(currencyCode)),
            option.getOrElse(() => false)
        )
);

export const getExchangeRate = createSelector(
    getStringProp('sourceCurrencyCode'),
    getStringProp('targetCurrencyCode'),
    getEnterpriseExchangeRates,
    getPlatformExchangeRates,
    (sourceCurrency, targetCurrency, enterpriseExchangeRates, platformExchangeRates) => {
        const enterpriseRate = find({ sourceCurrency, targetCurrency }, enterpriseExchangeRates);

        const getValue = getOrOne<ExchangeRate, 'value'>('value');

        if (isDefined(enterpriseRate)) {
            return getValue(enterpriseRate);
        }

        const platformRate = find({ sourceCurrency, targetCurrency }, platformExchangeRates);
        return getValue(platformRate);
    }
);
